import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MessageBusService } from '../../message-bus.service';
import { ShellClientService } from '../../shell-communication/shell-client.service';
import { DetectMethodChanges, getShortUUID } from '../../utils';
import { IHedgingGridCopilotFacade } from './copilot-facade';
import { AdjustmentCard, canPreview, placementRefusal, readProposal } from './available-adjustment-model';
import { AvailableAdjustmentsReply, GetAvailableAdjustments,
    RecordAdjustmentInspection } from './available-adjustment-operations';
import { HEDGE_BOOK_RESET_TOPIC } from '../hg-hedge-book-reset.message';
import { SessionService } from '../../authentication/session-service.service';
import { AdjustmentPanelQueries } from './adjustment-panel-queries';
import { indexAdjustments, missingAdjustments, reconcileAdjustmentCards } from './adjustment-card-reconciliation';
import { AdjustmentAvailabilityState } from './adjustment-availability-state';

interface PendingPreview {
    generation: number; card: AdjustmentCard; terminal: string; session: string;
}

@Component({selector: 'ets-hg-available-adjustments', templateUrl: './hg-available-adjustments.component.html',
    styleUrls: ['./hg-available-adjustments.component.scss'], changeDetection: ChangeDetectionStrategy.OnPush})
export class HgAvailableAdjustmentsComponent implements OnInit, OnChanges, OnDestroy {
    constructor(private readonly _changeDetector: ChangeDetectorRef, private readonly _shell: ShellClientService,
                private readonly _bus: MessageBusService, private readonly _session: SessionService) {}
    @Input() ownerId: string;
    @Input() portfolioId: string;
    @Input() targetItemId: string;
    @Input() targetRequest = 0;
    @Input() visible = false;
    @Input() ctx: IHedgingGridCopilotFacade;
    @Input() availability: AdjustmentAvailabilityState;
    @Output() expandedChange = new EventEmitter<boolean>();
    @Output() previewActiveChange = new EventEmitter<boolean>();
    @Output() matrixMutationChange = new EventEmitter<boolean>();
    expanded = false;
    cards: AdjustmentCard[] = [];
    error = '';
    private readonly _panelId = getShortUUID();
    get panelId(): string { return this.availability?.panelId || this._panelId; }
    private readonly _end = new Subject<void>();
    private _generation = 0;
    private _scope = '';
    private _querying = false;
    private _destroyed = false;
    private _timer: any;
    private _pending: PendingPreview = null;
    private _lastTarget: string;
    private _lastTargetRequest = -1;
    private readonly _inspected = new Set<string>();
    private readonly _queries = new AdjustmentPanelQueries();
    private readonly _inspectionRetries = new Map<string, ReturnType<typeof setTimeout>>();
    private _availableIds = new Set<string>();
    private readonly _modeItemIds = new Set<string>();
    private _freshMode = false;
    private _wasVisible = false;
    private _refreshAgain = false;
    private _modeRevision = 0;

    get availableCount(): number { return this._availableIds.size; }
    get showBanner(): boolean { return this.availableCount > 0 || this.expanded; }
    get loading(): boolean { return this._querying; }
    get visibleCards(): AdjustmentCard[] {
        return this.cards.filter(c => c.currentlyAvailable || this._modeItemIds.has(c.itemId));
    }
    get hiddenPreviews(): AdjustmentCard[] {
        return this.cards.filter(c => c.solution.state.previewed && !this.visibleCards.includes(c));
    }
    displayReference(c: AdjustmentCard): string | null { return this.visible && c.currentlyAvailable && canPreview(c.proposal) ? c.proposal.assessmentId : null; }
    trackCard(_: number, c: AdjustmentCard): string { return c.solution.id; }

    ngOnInit(): void {
        this._bus.of('Hg.HedgeMatrixChanged').pipe(takeUntil(this._end))
            .subscribe(() => this.synchronizeMatrixPreviews());
        if (this.availability) {
            this.availability.changed.pipe(takeUntil(this._end)).subscribe(() => {
                if (!this._querying) { this.refresh(false); }
            });
        } else {
            ['ActionBoardChangedDto', 'Hg.HedgeMatrixStrikeContextChanged', 'Hg.PortfolioSelected'].forEach(topic =>
                this._bus.of(topic).pipe(takeUntil(this._end)).subscribe(() => this.refresh()));
            this._timer = setInterval(() => this.refresh(), 5000);
        }
        this._bus.of(HEDGE_BOOK_RESET_TOPIC).pipe(takeUntil(this._end)).subscribe(() => this.onBookReset());
        this.refresh(false);
    }
    ngOnChanges(): void {
        const scope = `${this.ownerId}|${this.portfolioId}`;
        if (scope !== this._scope) {
            this._scope = scope;
            this.reset();
            this._lastTarget = null;
        }
        if (this.targetItemId && (this.targetItemId !== this._lastTarget || this.targetRequest !== this._lastTargetRequest)) {
            this._lastTarget = this.targetItemId;
            this._lastTargetRequest = this.targetRequest;
            this.expanded = true;
            ++this._modeRevision;
            this._freshMode = true;
            this._modeItemIds.clear();
            this.expandedChange.emit(true);
            this.refresh();
        }
        if (this.visible && !this._wasVisible && this.expanded) {
            ++this._modeRevision;
            this._freshMode = true; this._modeItemIds.clear(); this.refresh();
        }
        this._wasVisible = this.visible;
    }
    ngOnDestroy(): void {
        this._destroyed = true;
        clearInterval(this._timer);
        this.reset();
        this._end.next(); this._end.complete();
    }
    private reset(): void {
        ++this._generation;
        this._queries.cancelAll();
        this._inspectionRetries.forEach(timer => clearTimeout(timer));
        this._inspectionRetries.clear(); this._inspected.clear();
        this._querying = false;
        this._refreshAgain = false; this._freshMode = false; this._modeItemIds.clear();
        ++this._modeRevision;
        this._pending = null;
        const old = this.cards;
        this.cards = []; this._availableIds.clear(); this.error = ''; this.expanded = false;
        old.filter(c => c.solution.state.previewed).forEach(c => this.ctx?.removeCopilotLegs(c.solution.id).catch(() => {}));
        this.expandedChange.emit(false); this.previewActiveChange.emit(false);
    }
    @DetectMethodChanges()
    onBookReset(): void { this.reset(); this.refresh(); }

    @DetectMethodChanges()
    toggle(): void {
        ++this._modeRevision;
        this.expanded = !this.expanded;
        this._modeItemIds.clear();
        this._freshMode = this.expanded;
        if (!this.expanded) {
            if (this._pending) { this._pending.card.busy = false; this._pending = null; }
            this.cards = this.cards.filter(c => c.solution.state.previewed || c.busy);
            this._inspectionRetries.forEach(timer => clearTimeout(timer)); this._inspectionRetries.clear();
        }
        this.expandedChange.emit(this.expanded);
        this.refresh();
    }
    @DetectMethodChanges({isAsync: true})
    async refresh(force = true): Promise<void> {
        if (this._destroyed || !this.ownerId || !this.portfolioId || !this.ctx?.isEntitled()) { return; }
        if (this._querying) { this._refreshAgain = this._refreshAgain || (force && this._freshMode); return; }
        const generation = this._generation;
        const modeRevision = this._modeRevision;
        const owner = this.ownerId, portfolio = this.portfolioId;
        this._querying = true;
        this._changeDetector.detectChanges();
        try {
            const shared = this.availability ? await this.availability.refresh(force) : null;
            const reply = this.availability ? null : await this.query(
                new GetAvailableAdjustments(owner, portfolio, this.panelId, this.expanded ? this._lastTarget : null));
            if (generation !== this._generation || this._destroyed) { return; }
            if (modeRevision !== this._modeRevision) { return; }
            if (this.availability && !shared) { throw new Error('Adjustments Cannot be Confirmed.'); }
            const indexed = shared || indexAdjustments(reply, owner, portfolio);
            const entries = new Map(indexed.proposals);
            if (this.expanded && Array.from(indexed.availableIds).some(id => !this._availableIds.has(id))) {
                this._freshMode = true; this._modeItemIds.clear();
            }
            this._availableIds = new Set(indexed.availableIds);
            if (this._freshMode) {
                this.cards = this.cards.filter(c => c.solution.state.previewed || c.busy);
                this._freshMode = false;
            }
            const missing = missingAdjustments(this.cards, entries, this.expanded);
            // A worker pool bounds requests, not retained cards: every inspected/previewed
            // missing card remains visible even if its current proof cannot be loaded.
            let cursor = 0;
            await Promise.all(Array.from({length: Math.min(3, missing.length)}, async () => {
                while (cursor < missing.length && generation === this._generation && !this._destroyed) {
                    const card = missing[cursor++];
                    try {
                        const result = await this.query(new GetAvailableAdjustments(owner, portfolio, this.panelId, card.itemId));
                        if (result?.target?.actionItemId === card.itemId && !entries.has(card.itemId)) {
                            entries.set(card.itemId, readProposal(result.target, owner, portfolio));
                        }
                    } catch (_) { /* Retain the card below, with actions blocked. */ }
                }
            }));
            if (generation !== this._generation || this._destroyed) { return; }
            if (modeRevision !== this._modeRevision) { return; }
            this.cards = reconcileAdjustmentCards(this.cards, entries, this.expanded, () => 'available-' + getShortUUID())
                .map(update => {
                    Object.assign(update.card, {proposal: update.proposal, currentlyAvailable: update.currentlyAvailable,
                        message: update.message});
                    return update.card;
                });
            if (this.expanded) {
                this.cards.filter(c => c.currentlyAvailable).forEach(c => this._modeItemIds.add(c.itemId));
            }
            this.error = '';
        } catch (e) {
            if (generation === this._generation) {
                this.error = 'Adjustments Cannot be Confirmed. Retry.';
                this._availableIds.clear();
                this.cards.forEach(c => {
                    c.currentlyAvailable = false;
                    if (!c.solution.state.previewed) { c.proposal = {...c.proposal, health: 'unconfirmed'}; }
                });
            }
        } finally {
            if (generation === this._generation) {
                this._querying = false;
                if (this._refreshAgain) { this._refreshAgain = false; this.refresh(); }
            }
        }
    }
    private query(request: GetAvailableAdjustments): Promise<AvailableAdjustmentsReply> {
        return this._queries.query(cancelled => this._shell.processQuery<AvailableAdjustmentsReply>(request, '', cancelled));
    }
    @DetectMethodChanges()
    synchronizeMatrixPreviews(): void {
        if (!this.ctx?.hasAdjustmentPreview) { return; }
        let changed = false;
        this.cards.forEach(card => {
            if (!card.solution.state.previewed || this.ctx.hasAdjustmentPreview(card.solution.id)) { return; }
            this.ctx.forgetAdjustmentPreview?.(card.solution.id);
            card.solution.state.previewed = false;
            card.solution.state.applied = false;
            changed = true;
        });
        if (changed) { this.emitPreview(); }
    }
    @DetectMethodChanges({isAsync: true})
    async preview(c: AdjustmentCard): Promise<void> {
        if (c.busy || c.solution.state.applied) { return; }
        if (c.solution.state.previewed) {
            this.matrixMutationChange.emit(true);
            try { await this.ctx.removeCopilotLegs(c.solution.id); }
            finally { this.matrixMutationChange.emit(false); }
            c.solution.state.previewed = false;
            this.emitPreview(); this.refresh(); return;
        }
        if (!c.currentlyAvailable) { c.message = 'This Adjustment is no Longer Confirmed Available.'; return; }
        const refusal = placementRefusal(c.proposal, this.ctx);
        if (refusal) { c.message = refusal; return; }
        const terminal = this._session.loginResult?.ownTerminal?.terminalId;
        const session = this._session.sessionData?.authToken;
        if (!terminal || !session) { c.message = 'The Trading Session is Not Ready. Retry After Reconnecting.'; return; }
        if (this._pending) { this._pending.card.busy = false; }
        const pending: PendingPreview = {generation: this._generation, card: c, terminal, session};
        this._pending = pending; c.busy = true; c.message = '';
        const p = c.proposal;
        const ctx = this.ctx;
        try {
            const refusal = placementRefusal(p, ctx);
            if (refusal) { c.message = refusal; return; }
            this._changeDetector.detectChanges();
            this.matrixMutationChange.emit(true);
            let result;
            try {
                result = await ctx.applySolution(c.solution.id, c.solution.content.legs, c.solution.content.cards,
                    () => this.isCurrentPreview(pending) && this.ctx === ctx && c.currentlyAvailable && !placementRefusal(p, ctx));
            } finally { this.matrixMutationChange.emit(false); }
            if (!this.isCurrentPreview(pending) || this.ctx !== ctx) {
                if (result.applied) { await ctx.removeCopilotLegs(c.solution.id); }
                return;
            }
            c.solution.state.previewed = result.applied;
            if (result.applied) {
                ctx.bindAvailableAdjustment?.(c.solution.id, {panelInstanceId: this.panelId,
                    assessmentId: p.assessmentId, actionItemId: c.itemId});
            }
            c.message = result.applied ? '' : 'The Matrix Cannot Preview This Adjustment. Check Existing Changes and Loaded Contracts.';
            this.emitPreview();
        } catch (e) { c.message = e?.message || 'Preview Could Not be Prepared. Retry.'; }
        finally { c.busy = false; if (this._pending === pending) { this._pending = null; } }
    }
    @DetectMethodChanges()
    apply(c: AdjustmentCard): void {
        if (c.busy || !c.solution.state.previewed) { return; }
        c.solution.state.applied = !c.solution.state.applied; // ORION selection only; never acquisition/execution
    }
    inspected(reference: string): void {
        if (!reference || this._destroyed || !this.visible || !this.expanded || this._inspected.has(reference)) { return; }
        const retried = this._inspectionRetries.has(reference);
        clearTimeout(this._inspectionRetries.get(reference)); this._inspectionRetries.delete(reference);
        this._inspected.add(reference);
        this.recordInspection(reference, this._generation, retried);
    }
    private async recordInspection(reference: string, generation: number, retried: boolean): Promise<void> {
        try { await this._shell.processCommand(new RecordAdjustmentInspection(this.panelId, reference)); }
        catch (_) {
            if (retried || this._destroyed || generation !== this._generation) { return; }
            this._inspected.delete(reference);
            this._inspectionRetries.set(reference, setTimeout(() => {
                this._inspectionRetries.delete(reference);
                if (!this._destroyed && generation === this._generation && this.visible && this.expanded
                    && this.cards.some(c => c.proposal.assessmentId === reference)) {
                    this._inspected.add(reference);
                    this.recordInspection(reference, generation, true);
                }
            }, 5000));
        }
    }
    private isCurrentPreview(p: PendingPreview): boolean {
        return !!p && !this._destroyed && p.generation === this._generation && this._pending === p
            && this._session.sessionData?.authToken?.toLowerCase() === p.session.toLowerCase()
            && this._session.loginResult?.ownTerminal?.terminalId?.toLowerCase() === p.terminal.toLowerCase();
    }
    private emitPreview(): void { this.previewActiveChange.emit(this.cards.some(c => c.solution.state.previewed)); }
}
